import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Input } from 'reactstrap';
import config from '../../config';

const GOOGLE_MAPS_SCRIPT_SELECTOR = 'script[src*="maps.googleapis.com/maps/api/js"]';
const GOOGLE_MAPS_SCRIPT_ID = 'gophr-google-maps-api';
let googleMapsApiPromise = null;

const getWindowGoogle = () =>
  typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.places ? window.google : null;

const getComponentValue = (components, type, valueKey = 'long_name') => {
  if (!Array.isArray(components)) {
    return '';
  }

  const component = components.find(candidate => Array.isArray(candidate.types) && candidate.types.includes(type));

  if (!component) {
    return '';
  }

  return component[valueKey] || '';
};

const normalizeLocationValue = value => (typeof value === 'string' ? value.trim() : '');

export const buildCoordinateFallbackLabel = (lat, lng) => {
  const safeLat = typeof lat === 'number' ? lat.toFixed(6) : String(lat || '');
  const safeLng = typeof lng === 'number' ? lng.toFixed(6) : String(lng || '');

  return `Custom Location (${safeLat}, ${safeLng})`;
};

export const isCoordinateFallbackLabel = value => typeof value === 'string' && value.startsWith('Custom Location (');

export const normalizeGoogleAddressResult = placeResult => {
  if (!placeResult) {
    return null;
  }

  const geometryLocation = placeResult.geometry && placeResult.geometry.location ? placeResult.geometry.location : null;
  const lat = geometryLocation && typeof geometryLocation.lat === 'function' ? geometryLocation.lat() : placeResult.lat;
  const lng = geometryLocation && typeof geometryLocation.lng === 'function' ? geometryLocation.lng() : placeResult.lng;

  if (typeof lat !== 'number' || typeof lng !== 'number') {
    return null;
  }

  const addressComponents = Array.isArray(placeResult.address_components) ? placeResult.address_components : [];
  const streetNumber = getComponentValue(addressComponents, 'street_number');
  const route = getComponentValue(addressComponents, 'route');
  const premise = getComponentValue(addressComponents, 'premise');
  const subpremise = getComponentValue(addressComponents, 'subpremise');
  const neighborhood = getComponentValue(addressComponents, 'neighborhood');
  const locality = getComponentValue(addressComponents, 'locality')
    || getComponentValue(addressComponents, 'postal_town')
    || getComponentValue(addressComponents, 'sublocality')
    || getComponentValue(addressComponents, 'administrative_area_level_2');
  const state = getComponentValue(addressComponents, 'administrative_area_level_1', 'short_name');
  const zip = getComponentValue(addressComponents, 'postal_code');
  const zipSuffix = getComponentValue(addressComponents, 'postal_code_suffix');
  const country = getComponentValue(addressComponents, 'country');
  const countryCode = getComponentValue(addressComponents, 'country', 'short_name');

  let address1 = normalizeLocationValue([streetNumber, route].filter(Boolean).join(' '));
  if (!address1) {
    address1 = normalizeLocationValue(premise) || normalizeLocationValue(placeResult.name) || normalizeLocationValue(neighborhood);
  }

  const address2 = normalizeLocationValue(subpremise);
  const normalizedZip = [zip, zipSuffix].filter(Boolean).join('-');
  const fallbackLabel = buildCoordinateFallbackLabel(lat, lng);
  const formattedAddress = normalizeLocationValue(placeResult.formatted_address) || address1 || fallbackLabel;

  return {
    formattedAddress,
    fallbackLabel,
    lat,
    lng,
    address1: address1 || fallbackLabel,
    address2,
    city: normalizeLocationValue(locality),
    state: normalizeLocationValue(state),
    zip: normalizeLocationValue(normalizedZip),
    country: normalizeLocationValue(country),
    countryCode: normalizeLocationValue(countryCode),
  };
};

const hasRequiredAddressFields = normalizedPlace => {
  if (!normalizedPlace || typeof normalizedPlace !== 'object') {
    return false;
  }

  const countryCode = normalizeLocationValue(normalizedPlace.countryCode || normalizedPlace.country).toUpperCase();
  const hasBaseFields = Boolean(
    normalizeLocationValue(normalizedPlace.address1)
      && normalizeLocationValue(normalizedPlace.city)
      && normalizeLocationValue(normalizedPlace.state)
      && countryCode
  );

  if (!hasBaseFields) {
    return false;
  }

  if (countryCode === 'US' || countryCode === 'CA' || countryCode === 'MX') {
    return Boolean(normalizeLocationValue(normalizedPlace.zip));
  }

  return true;
};

export const reverseGeocodeLocation = async ({ lat, lng, google }) => {
  const activeGoogle = google || getWindowGoogle();

  if (!activeGoogle || !activeGoogle.maps || typeof activeGoogle.maps.Geocoder !== 'function') {
    throw new Error('Google Maps geocoder is unavailable.');
  }

  return new Promise((resolve, reject) => {
    const geocoder = new activeGoogle.maps.Geocoder();

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && Array.isArray(results) && results.length > 0) {
        const normalized = normalizeGoogleAddressResult(results[0]);
        if (normalized) {
          resolve(normalized);
          return;
        }
      }

      if (status === 'ZERO_RESULTS') {
        resolve({
          formattedAddress: buildCoordinateFallbackLabel(lat, lng),
          fallbackLabel: buildCoordinateFallbackLabel(lat, lng),
          lat,
          lng,
          address1: buildCoordinateFallbackLabel(lat, lng),
          address2: '',
          city: '',
          state: '',
          zip: '',
          country: '',
          countryCode: '',
        });
        return;
      }

      reject(new Error(`Reverse geocoding failed: ${status}`));
    });
  });
};

const waitForGoogleMapsPlaces = existingScript =>
  new Promise((resolve, reject) => {
    const maxWaitMs = 15000;
    const pollIntervalMs = 100;
    const startedAt = Date.now();

    const cleanup = () => {
      if (existingScript) {
        existingScript.removeEventListener('load', checkReadyState);
        existingScript.removeEventListener('error', handleError);
      }
      clearInterval(pollTimer);
    };

    const handleError = () => {
      cleanup();
      reject(new Error('Google Maps failed to load.'));
    };

    const checkReadyState = () => {
      const google = getWindowGoogle();
      if (google) {
        cleanup();
        resolve(google);
        return;
      }

      if (Date.now() - startedAt >= maxWaitMs) {
        cleanup();
        reject(new Error('Google Maps Places library did not become ready in time.'));
      }
    };

    const pollTimer = window.setInterval(checkReadyState, pollIntervalMs);

    if (existingScript) {
      existingScript.addEventListener('load', checkReadyState);
      existingScript.addEventListener('error', handleError);
    }

    checkReadyState();
  });

export const loadGoogleMapsApi = () => {
  const availableGoogle = getWindowGoogle();
  if (availableGoogle) {
    return Promise.resolve(availableGoogle);
  }

  if (googleMapsApiPromise) {
    return googleMapsApiPromise;
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Google Maps can only load in the browser.'));
  }

  const existingScript = document.querySelector(GOOGLE_MAPS_SCRIPT_SELECTOR);
  if (existingScript) {
    googleMapsApiPromise = waitForGoogleMapsPlaces(existingScript).catch(error => {
      googleMapsApiPromise = null;
      throw error;
    });
    return googleMapsApiPromise;
  }

  const script = document.createElement('script');
  script.id = GOOGLE_MAPS_SCRIPT_ID;
  script.async = true;
  script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
    config.config.GOOGLE_MAP.accessKey
  )}&libraries=places&loading=async`;

  googleMapsApiPromise = new Promise((resolve, reject) => {
    script.addEventListener('load', () => {
      waitForGoogleMapsPlaces(script).then(resolve).catch(reject);
    });
    script.addEventListener('error', () => {
      reject(new Error('Google Maps failed to load.'));
    });

    document.body.appendChild(script);
  }).catch(error => {
    googleMapsApiPromise = null;
    throw error;
  });

  return googleMapsApiPromise;
};

const GoogleMapAutoComplete = props => {
  const [hidden, setHidden] = useState(false);
  const [error, setError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [query, setQuery] = useState(props.query || '');
  const [queryResults, setQueryResults] = useState([]);
  const [resultClicked, setResultClicked] = useState(false);
  const [index] = useState(props.index || 0);
  const [googleApi, setGoogleApi] = useState(() => props.google || getWindowGoogle());

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const getCountryFromComponents = addressComponents => {
    let country = null;
    let countryCode = null;

    if (Array.isArray(addressComponents)) {
      for (const component of addressComponents) {
        if (component.types && component.types.includes('country')) {
          country = component.long_name || component.longText || null;
          countryCode = component.short_name || component.shortText || null;
          break;
        }
      }
    }

    return { country, countryCode };
  };

  const updateQuery = event => {
    setResultClicked(false);
    setQuery(event.target.value);
    setHidden(false);

    // Call the function to perform the Autocomplete search
    performAutocompleteSearch(event.target.value);
  };

  const performAutocompleteSearch = async query => {
    const google = googleApi || props.google;

    if (query.length < 3) {
      // Reset the error state and query results when the query is too short
      setError(false);
      setErrorMsg('');
      setQueryResults([]);
      return;
    }

    if (!google || !google.maps || !google.maps.places) {
      setError(true);
      setErrorMsg('Google Maps is still loading. Please try again.');
      setQueryResults([]);
      return;
    }

    try {
      const autocompleteService = new google.maps.places.AutocompleteService();
      const request = {
        input: query,
        componentRestrictions: props.country ? { country: props.country } : {}
      };

      autocompleteService.getPlacePredictions(request, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          setError(false);
          setErrorMsg('');
          setQueryResults(
            (results || []).map((result, resultIndex) => ({
              ...result,
              key: result.place_id || `${query}-${resultIndex}`
            }))
          );
        } else {
          setError(true);
          setErrorMsg('There was a problem. Please try again.');
          setQueryResults([]);
        }
      });
    } catch (searchError) {
      setError(true);
      setErrorMsg('There was a problem. Please try again.');
      setQueryResults([]);
    }
  };

  const resetSearch = () => {
    setQueryResults([]);
  };

  const onSuggestionSelect = async place => {
    setResultClicked(true);
    const google = googleApi || props.google;

    if (!google || !google.maps || !google.maps.places) {
      setError(true);
      setErrorMsg('Google Maps is still loading. Please try again.');
      return;
    }

    try {
      const placesService = new google.maps.places.PlacesService(document.createElement('div'));
      const request = {
        placeId: place.place_id,
        fields: ['name', 'geometry', 'formatted_address', 'address_components']
      };

      placesService.getDetails(request, (placeDetails, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          const normalizedPlace = normalizeGoogleAddressResult(placeDetails);
          if (normalizedPlace && !hasRequiredAddressFields(normalizedPlace)) {
            setError(true);
            setErrorMsg('Please select a complete street address from the dropdown.');
            resetSearch();
            return;
          }

          const lat = normalizedPlace ? normalizedPlace.lat : placeDetails.geometry.location.lat();
          const lng = normalizedPlace ? normalizedPlace.lng : placeDetails.geometry.location.lng();
          const formattedAddress = normalizedPlace ? normalizedPlace.formattedAddress : placeDetails.formatted_address;
          const { country, countryCode } = normalizedPlace || getCountryFromComponents(placeDetails.address_components);

          setQuery(formattedAddress);
          setHidden(true);
          setError(false);
          setErrorMsg('');
          resetSearch();
          props.onSuggestionSelect(normalizedPlace || formattedAddress, lat, lng, index, country, countryCode);
        } else {
          setError(true);
          setErrorMsg('Could not load place details. Please try again.');
        }
      });
    } catch (selectionError) {
      setError(true);
      setErrorMsg('Could not load place details. Please try again.');
    }
  };

  useEffect(() => {
    if (!resultClicked && props.query !== undefined && props.query !== query) {
      setQuery(props.query || '');
    }
  }, [props.query]);

  useEffect(() => {
    let isMounted = true;

    loadGoogleMapsApi()
      .then(google => {
        if (isMounted) {
          setGoogleApi(google);
          setError(false);
          setErrorMsg('');
        }
      })
      .catch(() => {
        if (isMounted) {
          setError(true);
          setErrorMsg('Google Maps failed to load. Please refresh and try again.');
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleOutsideClick = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        if (!hidden) {
          setHidden(true);
        }
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [hidden]);

  return (
    <div ref={wrapperRef}>
      <Input
        innerRef={inputRef}
        autoComplete="off"
        placeholder={props.placeholder || 'Search'}
        className={props.inputClass ? props.inputClass + ' react-google-ac-input' : 'react-google-ac-input'}
        onChange={updateQuery}
        value={(resultClicked ? props.value : query) || props.defaultValue || ''}
        readOnly={Boolean(props.readOnly)}
        type="text"
        id={props.id || 'google-autocomplete'}
        onFocus={() => {
          setHidden(false);
        }}
      />
      {!hidden && (queryResults.length > 0 || error) && (
        <div className="react-google-ac-menu" style={{ display: 'block' }}>
          {queryResults.map((place, i) => {
            return (
              <div
                className="react-google-ac-suggestion"
                onClick={() => onSuggestionSelect(place)}
                key={place.key || i}
                style={{ fontSize: props.fontSize }}
              >
                {place.description}
              </div>
            );
          })}

          {error && <div className="react-google-ac-suggestion">{errorMsg}</div>}
        </div>
      )}
    </div>
  );
};

GoogleMapAutoComplete.propTypes = {
  inputClass: PropTypes.string,
  placeholder: PropTypes.string,
  onSuggestionSelect: PropTypes.func.isRequired,
  country: PropTypes.string,
  query: PropTypes.string,
  resetSearch: PropTypes.bool,
  google: PropTypes.object,
  id: PropTypes.string
};

export default GoogleMapAutoComplete;
